import {GroupEditingLevelTypes, GroupResourceOption} from '../../../graphql/groups.datatypes';
import {makeStyles} from '@material-ui/styles';
import {useSnackbar} from 'notistack';
import React, {useEffect, useState} from 'react';
import {
  Button,
  Card,
  CardActions,
  CardContent,
  colors,
  Grid,
  Modal,
  Switch,
  TextField,
  Theme,
  Typography
} from '@material-ui/core';
import anyLoading from '../../../utils/anyLoading';
import updateGroupResourceOption, {UpdateGroupResourceOptionInput} from './updateGroupResourceOption.api';
import clsx from 'clsx';
import {LoadingOverlay} from '../../../components/LoadingOverlay';
import {T} from 'react-polyglot-hooks';
import {getHandleFieldChange, getHasError} from '../../../utils/formHelpers';
import validate from 'validate.js';
import {ResourceOptionActions} from '../../../graphql/resources.datatypes';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    outline: 'none',
    boxShadow: theme.shadows[20],
    width: 700,
    maxHeight: '90%',
    overflowY: 'auto',
    maxWidth: '95%'
  },
  container: {
    marginTop: theme.spacing(3)
  },
  actions: {
    justifyContent: 'flex-end'
  },
  deleteButton: {
    color: theme.palette.white,
    backgroundColor: colors.red[600],
    '&:hover': {
      backgroundColor: colors.red[900]
    }
  },
  submitButton: {
    color: theme.palette.white,
    backgroundColor: colors.green[600],
    '&:hover': {
      backgroundColor: colors.green[900]
    }
  }
}));

export const GroupOptionEditModal = (props: {
  className?: string,
  onClose: () => void,
  onUpdate?: () => void,
  open: boolean,
  groupOption: GroupResourceOption
}) => {
  const {open, onClose, onUpdate, groupOption, ...rest} = props;
  const classes = useStyles();
  const [formState, setFormState] = useState({...groupOption});
  const [sendUpdateGroupResourceOption, updateOptionData] = updateGroupResourceOption();
  const loadingData = anyLoading(updateOptionData);
  const {enqueueSnackbar} = useSnackbar();

  //2. Setup helper functions for form
  const [errors, setErrors] = useState({
    isValid: false,
    show_hidden_override: formState.hidden_override !== null,
    show_additional_charge_override: formState.additional_charge_override !== null,
    show_unit_cost_override: formState.unit_cost_override !== null,
    show_action_type_override: formState.action_type_override !== null,
    errors: {}
  });
  const handleFieldChange = getHandleFieldChange(setFormState);
  const hasError = getHasError(errors);

  //3. setup validation and error hook
  const validateSchema: any = {};

  useEffect(() => {
    const newErrors = validate(formState, validateSchema);

    setErrors(state => ({
      ...state,
      isValid: !newErrors,
      errors: newErrors || {}
    }));
  }, [formState]);

  const handleUpdateGroupOption = () => {

    const sanitisedData: UpdateGroupResourceOptionInput = {
      data: {
        id: formState.id,
        editing_level: formState.editing_level,
        view_level: formState.view_level,
        unit_cost_override: errors.show_unit_cost_override ? formState.unit_cost_override : null,
        action_type_override: errors.show_action_type_override ? formState.action_type_override : null,
        hidden_override: errors.show_hidden_override ? formState.hidden_override : null,
        additional_charge_override: errors.show_additional_charge_override ? formState.additional_charge_override : null
      }
    };

    sendUpdateGroupResourceOption({
      variables: {...sanitisedData},
    })
        .then(() => {
          enqueueSnackbar('Group Option Updated.', {variant: 'success'});
          onClose();
          if (onUpdate) onUpdate();
        })
        .catch(() => {
          enqueueSnackbar('Group Option Update Failed!', {variant: 'error'});
          onClose();
        });
  };

  if (!open) {
    return null;
  }

  return (
      <Modal onClose={onClose} open={open}>
        <Card {...rest} className={clsx(classes.root)}>
          <form>
            <LoadingOverlay loading={loadingData.loading}>
              <CardContent>
                <Typography gutterBottom variant="h3">Edit Group Option: {groupOption.option.internal_name}</Typography>

                <Grid container spacing={3}>

                  <Grid item xs={12}>
                    <TextField
                        {...hasError('view_level')}
                        label="View Level Permissions"
                        fullWidth
                        onChange={handleFieldChange}
                        select
                        name="view_level"
                        // eslint-disable-next-line react/jsx-sort-props
                        SelectProps={{native: true}}
                        value={formState.view_level}
                        InputLabelProps={{
                          shrink: true,
                        }}
                        variant="outlined"
                    >
                      {GroupEditingLevelTypes.map((type, i) => (
                          <option key={type.name} value={i}>
                            {type.name}
                          </option>
                      ))}
                    </TextField>
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                        {...hasError('editing_level')}
                        label="Editing Level Permissions"
                        fullWidth
                        onChange={handleFieldChange}
                        select
                        name="editing_level"
                        // eslint-disable-next-line react/jsx-sort-props
                        SelectProps={{native: true}}
                        value={formState.editing_level}
                        InputLabelProps={{
                          shrink: true,
                        }}
                        variant="outlined"
                    >
                      {GroupEditingLevelTypes.map((type, i) => (
                          <option key={type.name} value={i}>
                            {type.name}
                          </option>
                      ))}
                    </TextField>
                  </Grid>

                  <Grid item md={12} xs={12}>
                    <Grid component="label" container alignItems="center" spacing={1}>
                      <Grid item>
                        <Switch
                            checked={errors.show_action_type_override}
                            color="secondary"
                            edge="start"
                            name="show_action_type_override"
                            onChange={(event, checked) => {
                              setErrors({...errors, show_action_type_override: checked});
                              setFormState({...formState, action_type_override: checked ? 0 : undefined});
                            }}
                            value={errors.show_action_type_override}
                        />
                      </Grid>
                      <Grid item><Typography variant="h5">Override Option Action?</Typography></Grid>
                    </Grid>
                  </Grid>
                  {errors.show_action_type_override && (
                      <React.Fragment>
                        <Grid item xs={1}/>
                        <Grid item xs={10}>
                          <TextField
                              {...hasError('action_type_override')}
                              fullWidth
                              label="Action"
                              name="action_type_override"
                              onChange={handleFieldChange}
                              select
                              // eslint-disable-next-line react/jsx-sort-props
                              SelectProps={{native: true}}
                              value={String(formState.action_type_override)}
                              variant="outlined"
                              InputLabelProps={{
                                shrink: true,
                              }}
                          >
                            {ResourceOptionActions.map((action, i) => (
                                <option key={i} value={i}>
                                  {action.name}
                                </option>
                            ))}
                          </TextField>
                        </Grid>
                      </React.Fragment>
                  )}

                  <Grid item md={12} xs={12}>
                    <Grid component="label" container alignItems="center" spacing={1}>
                      <Grid item>
                        <Switch
                            checked={errors.show_unit_cost_override}
                            color="secondary"
                            edge="start"
                            name="show_unit_cost_override"
                            onChange={(event, checked) => {
                              setErrors({...errors, show_unit_cost_override: checked});
                              setFormState({...formState, unit_cost_override: checked ? 0 : undefined});
                            }}
                            value={errors.show_unit_cost_override}
                        />
                      </Grid>
                      <Grid item><Typography variant="h5">Override Option Cost?</Typography></Grid>
                    </Grid>
                  </Grid>
                  {errors.show_unit_cost_override && (
                      <React.Fragment>
                        <Grid item xs={1}/>
                        <Grid item xs={10}>
                          <TextField
                              {...hasError('unit_cost_override')}
                              fullWidth
                              label="unit_cost_override"
                              name="unit_cost_override"
                              onChange={handleFieldChange}
                              value={formState.unit_cost_override}
                              variant="outlined"
                          />
                        </Grid>
                      </React.Fragment>
                  )}

                  <Grid item md={12} xs={12}>
                    <Grid component="label" container alignItems="center" spacing={1}>
                      <Grid item>
                        <Switch
                            checked={errors.show_hidden_override}
                            color="secondary"
                            edge="start"
                            name="show_hidden_override"
                            onChange={(event, checked) => {
                              setErrors({...errors, show_hidden_override: checked});
                              setFormState({...formState, hidden_override: checked ? false : undefined});
                            }}
                            value={errors.show_hidden_override}
                        />
                      </Grid>
                      <Grid item><Typography variant="h5">Override Hidden On Invoice?</Typography></Grid>
                    </Grid>
                  </Grid>
                  {errors.show_hidden_override && (
                      <React.Fragment>
                        <Grid item xs={1}/>
                        <Grid item xs={11}>
                          <Grid component="label" container alignItems="center" spacing={1}>
                            <Grid item>
                              <Switch
                                  checked={formState.hidden_override}
                                  color="secondary"
                                  edge="start"
                                  name="hidden_override"
                                  onChange={handleFieldChange}
                                  value={formState.hidden_override}
                              />
                            </Grid>
                            <Grid item><Typography variant="h5">Hide on Booking Form and Invoice?</Typography></Grid>
                          </Grid>
                        </Grid>
                      </React.Fragment>
                  )}

                  <Grid item md={12} xs={12}>
                    <Grid component="label" container alignItems="center" spacing={1}>
                      <Grid item>
                        <Switch
                            checked={errors.show_additional_charge_override}
                            color="secondary"
                            edge="start"
                            name="show_additional_charge_override"
                            onChange={(event, checked) => {
                              setErrors({...errors, show_additional_charge_override: checked});
                              setFormState({...formState, additional_charge_override: checked ? false : undefined});
                            }}
                            value={errors.show_additional_charge_override}
                        />
                      </Grid>
                      <Grid item><Typography variant="h5">Override Additional Charge?</Typography></Grid>
                    </Grid>
                  </Grid>
                  {errors.show_additional_charge_override && (
                      <React.Fragment>
                        <Grid item xs={1}/>
                        <Grid item xs={11}>
                          <Grid component="label" container alignItems="center" spacing={1}>
                            <Grid item>
                              <Switch
                                  checked={formState.additional_charge_override}
                                  color="secondary"
                                  edge="start"
                                  name="additional_charge_override"
                                  onChange={handleFieldChange}
                                  value={formState.additional_charge_override}
                              />
                            </Grid>
                            <Grid item><Typography variant="h5">Show as additional charge on
                              invoice?</Typography></Grid>
                          </Grid>
                        </Grid>
                      </React.Fragment>
                  )}


                </Grid>

              </CardContent>
              <CardActions className={classes.actions}>
                <Button onClick={() => {
                  onClose();
                }} variant="contained">
                  <T phrase="Cancel"/>
                </Button>
                <Button className={classes.submitButton} onClick={handleUpdateGroupOption} variant="contained">
                  <T phrase="Confirm"/>
                </Button>
              </CardActions>
            </LoadingOverlay>
          </form>
        </Card>
      </Modal>
  );
};

export default GroupOptionEditModal;
